import { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { useAppStorage, useObservable } from '@aesop-fables/scrinium';
import { Column } from '../components';
import PricebookRowLabel from '../components/PricebookRowLabel';
import { LicenseAgreementData } from '../api/apis/LicenseAgreementApi';
import { LicenseAgreementAcceptLogData } from '../api/apis/LicenseAgreementAcceptLogApi';
import { SubscriptionData } from '../api/apis/SubscriptionApi';
import { LicenseCompartments, licenseStorageKey } from '../data/license';
import {
  licenseAgreementAcceptLogCompartments,
  licenseAgreementAcceptLogStorageKey,
} from '../data/licenseAgreementAcceptLog';
import { ProductTypeCompartments, productTypeStorageKey } from '../data/productType';
import { SubscriptionCompartments, subscriptionStorageKey } from '../data/subscription';
import { GroupFeatureMapCompartments, groupFeatureMapStorageKey } from '../data/groupFeatureMap';
import { GroupFeatureMapData } from '../models/GroupFeatureMapData';
import { PricebookBundle, PaymentTypeEnum } from '../models/PricebookData';
import { filterByPayer, formatDateMMDDYYYY } from '../helpers/utilityFunctions';
import { getActivePrice, hasAvailablePackages } from '../helpers/pricebookUtils';
import { useBasePricebookOptions, usePricebookSelections } from './usePricebookOptions';

export function useLicenseAgreements(onViewDetails: (pricebook: PricebookBundle) => void) {
  const appStorage = useAppStorage();
  const productTypeDataCache = appStorage.retrieve<ProductTypeCompartments>(productTypeStorageKey);
  const licenseDataCache = appStorage.retrieve<LicenseCompartments>(licenseStorageKey);
  const licenseAgreementAcceptLogDataCache =
    appStorage.retrieve<licenseAgreementAcceptLogCompartments>(licenseAgreementAcceptLogStorageKey);
  const subscriptionDataCache =
    appStorage.retrieve<SubscriptionCompartments>(subscriptionStorageKey);
  const groupFeatureMapDataCache =
    appStorage.retrieve<GroupFeatureMapCompartments>(groupFeatureMapStorageKey);

  const paidBy = useObservable(productTypeDataCache.observe$<string>('paidBy'));
  const licenseAgreements = useObservable(
    licenseDataCache.observe$<LicenseAgreementData[]>('licenseAgreement'),
  );
  const licenseAgreementAcceptLog = useObservable(
    licenseAgreementAcceptLogDataCache.observe$<Map<number, LicenseAgreementAcceptLogData>>(
      'licenseAgreementAcceptLog',
    ),
  );

  const licenseInitialized = useObservable(
    licenseDataCache.findCompartment('licenseAgreement').initialized$,
  );
  const acceptLogInitialized = useObservable(
    licenseAgreementAcceptLogDataCache.findCompartment('licenseAgreementAcceptLog').initialized$,
  );
  const tableDataReady = licenseInitialized && acceptLogInitialized;

  const subscriptions = useObservable(
    subscriptionDataCache.observe$<SubscriptionData[]>('subscription'),
  );
  const groupFeatureMapData = useObservable(
    groupFeatureMapDataCache.observe$<GroupFeatureMapData>('groupFeatureMap'),
  );

  const pricebookOptions = useBasePricebookOptions();
  const pricebookSelections = usePricebookSelections();

  const [packagesAvailable, setPackagesAvailable] = useState(false);
  useEffect(() => {
    if (pricebookOptions) {
      const filteredPricebook = filterByPayer(pricebookOptions, paidBy);
      setPackagesAvailable(hasAvailablePackages(filteredPricebook, pricebookSelections));
    }
  }, [paidBy, pricebookOptions, pricebookSelections]);

  const pendingAgreement = licenseAgreements?.find(
    licenseAgreement => licenseAgreement.docStatus && !licenseAgreement.hasAccepted,
  );
  const activeLicenses = licenseAgreements?.filter(
    license => license.hasAccepted && license.docStatus,
  );
  const zeroActive = !activeLicenses || activeLicenses.length === 0;
  const meteredPayment = activeLicenses?.some(
    agreement => agreement.pricebookBundleDto.pricebook.paymentType === PaymentTypeEnum.Metered,
  );

  const getAcceptLogEntry = useCallback(
    (docId: number) => {
      const log = licenseAgreementAcceptLog?.get(docId);
      return {
        signingAdmin: `${log?.user?.firstName ?? ''} ${log?.user?.lastName ?? ''}`.trim(),
        cancellingAdmin:
          `${log?.cancelledByUser?.firstName ?? ''} ${log?.cancelledByUser?.lastName ?? ''}`.trim(),
        cancellationDate: log?.dateCancelled ? formatDateMMDDYYYY(log.dateCancelled) : '',
      };
    },
    [licenseAgreementAcceptLog],
  );

  const toTableRow = useCallback(
    (licenseAgreement: LicenseAgreementData) => {
      const { signingAdmin } = getAcceptLogEntry(licenseAgreement.id);
      const pricebook = licenseAgreement.pricebookBundleDto?.pricebook;
      const selectedPrice = getActivePrice(licenseAgreement.pricebookBundleDto?.prices ?? [], {
        preferOneTime: true,
      });
      return {
        id: licenseAgreement.id,
        _artIcon: (
          <img
            src={licenseAgreement.pricebookBundleDto.pricebook.artUrl}
            alt='Product Art'
            style={{ width: '30px', height: '30px', objectFit: 'contain' }}
          />
        ),
        product: (
          <PricebookRowLabel
            name={pricebook?.pricebookName ?? ''}
            price={selectedPrice}
            shortDescription={pricebook?.internalAdminDescription}
            metadata={pricebook?.metadata}
            onNameClick={() => onViewDetails(licenseAgreement.pricebookBundleDto)}
          />
        ),
        startDate: (
          <Column>
            {signingAdmin && (
              <Typography variant='p16' color='secondary.dark'>
                Signed by {signingAdmin}
              </Typography>
            )}
            <Typography variant='p12' color='secondary.main'>
              {formatDateMMDDYYYY(licenseAgreement.docDate)}
            </Typography>
          </Column>
        ),
        _startDate: formatDateMMDDYYYY(licenseAgreement.docDate),
      };
    },
    [getAcceptLogEntry, onViewDetails],
  );

  const activeTableData = useMemo(() => {
    if (!tableDataReady) return [];

    return (licenseAgreements ?? [])
      .filter(agreement => agreement.docStatus && agreement.hasAccepted)
      .map(agreement => ({
        ...toTableRow(agreement),
        isActive: agreement.docStatus,
      }));
  }, [tableDataReady, licenseAgreements, toTableRow]);

  const expiredTableData = useMemo(() => {
    if (!tableDataReady) return [];

    return (licenseAgreements ?? [])
      .filter(agreement => !agreement.docStatus && agreement.hasAccepted)
      .map(agreement => {
        const { cancellingAdmin, cancellationDate } = getAcceptLogEntry(agreement.id);
        return {
          ...toTableRow(agreement),
          endDate:
            cancellingAdmin || cancellationDate ? (
              <Column>
                {cancellingAdmin && (
                  <Typography variant='p16' color='secondary.dark'>
                    Cancelled by {cancellingAdmin}
                  </Typography>
                )}
                {cancellationDate && (
                  <Typography variant='p12' color='secondary.main'>
                    {cancellationDate}
                  </Typography>
                )}
              </Column>
            ) : (
              <Typography variant='p16' color='secondary.main'>
                —
              </Typography>
            ),
          _endDate: cancellationDate,
          isActive: false,
        };
      });
  }, [tableDataReady, licenseAgreements, getAcceptLogEntry, toTableRow]);

  const failedInvoices = useMemo(
    () => subscriptions?.filter(subscription => subscription.paymentFailed),
    [subscriptions],
  );

  return {
    licenseAgreements,
    paidBy,
    meteredPayment,
    zeroActive,
    pendingAgreement,
    groupFeatureMapData,
    packagesAvailable,
    failedInvoices,
    activeTableData,
    expiredTableData,
    tableDataReady,
  };
}
